<template>
  <div class="v-step" :class="{ 'is-active': isActive, 'is-current': isCurrent, 'is-disabled': disabled, [`v-step--${type}`]: type }" @click="onClick">
    <div class="v-step__content">
      <div class="v-step__dot">
        <slot name="dot">
          <VIcon v-if="activeIcon && !disabled" :icon="activeIcon" :size="10" />
        </slot>
      </div>

      <div class="v-step__body">
        <slot>
          <div v-if="title" class="v-step__title" v-text="title" />
          <div v-if="steps.props.vertical && description" class="v-step__description" v-text="description" />
        </slot>
      </div>
    </div>

    <div class="v-step__line" />
  </div>
</template>

<script lang="ts">
let counter = 0
</script>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import { type VIconIcon, type VStepType } from '../../types.ts'
import { findAndSpliceArray } from '../../utils/helpers.ts'
import { $stepsKey, injectRequired } from '../../utils/injections.ts'
import VIcon from '../Icon/VIcon.vue'

interface Props {
  disabled?: boolean
  title?: string
  description?: string
  icon?: VIconIcon
  type?: VStepType
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  title: '',
  description: '',
  icon: undefined,
  type: undefined,
})

defineSlots<{
  default?(): any
  dot?(): any
}>()

const steps = injectRequired($stepsKey)

const index = ref(-1)

const currentStep = computed(() => steps.model.value)
const isActive = computed(() => index.value < currentStep.value)
const isCurrent = computed(() => currentStep.value === index.value)
const activeIcon = computed(() => props.icon ?? (isActive.value ? 'Check' : undefined))

const id = counter++
steps.steps.value.push(id)
index.value = steps.steps.value.indexOf(id)

onBeforeUnmount(() => findAndSpliceArray(steps.steps.value, (value) => value === id))

watch(
  () => steps.steps.value,
  (value) => (index.value = value?.indexOf(id) as number),
  { immediate: true },
)

function onClick() {
  if (props.disabled || !steps?.props.clickable) {
    return
  }

  steps.model.value = index.value
}
</script>

<style>
.v-step {
  display: flex;
  background-color: transparent;

  &.is-active {
    color: var(--color-grey-40);

    .v-step__dot {
      background-color: var(--color-primary);
      color: var(--color-grey-40);
      border: none;

      .v-icon * {
        stroke: #fff;
      }
    }

    .v-step__title {
      text-decoration: line-through;
    }

    .v-step__body {
      font-weight: 500;
      color: var(--color-primary);
      opacity: 40%;
    }
  }

  &.is-current .v-step__dot {
    color: #fff;

    .v-icon * {
      stroke: #fff;
    }
  }

  &.is-disabled {
    cursor: default;
    color: var(--color-grey-40);

    .v-step__dot {
      background-color: var(--color-blue-1);
      color: var(--color-text-primary);
      border: 1px dashed var(--color-grey-40);
    }
  }

  &.v-step--black .v-step__dot {
    background-color: #000;
    border: none;
    color: #fff;
  }

  &.v-step--black .v-step__dot .v-icon * {
    stroke: #fff;
  }

  &.v-step--white .v-step__dot {
    background-color: #fff;
    border: none;
    color: #000;
  }

  &.v-step--white .v-step__dot .v-icon * {
    stroke: #000;
  }

  &.v-step--green .v-step__dot {
    background-color: var(--color-green);
    border: none;
    color: #fff;
  }

  &.v-step--green .v-step__dot .v-icon * {
    stroke: #fff;
  }

  &.v-step--orange .v-step__dot {
    background-color: var(--color-orange);
    border: none;
    color: #fff;
  }

  &.v-step--orange .v-step__dot .v-icon * {
    stroke: #fff;
  }

  &.v-step--red .v-step__dot {
    background-color: var(--color-red);
    border: none;
    color: #fff;
  }

  &.v-step--red .v-step__dot .v-icon * {
    stroke: #fff;
  }
}

.v-steps--clickable .v-step {
  cursor: pointer;

  &:hover:not(.is-active, .is-current, .is-disabled) .v-step__dot {
    border-color: black;
    color: black;
  }
}

/* Apply vertical specific style. */
.v-steps--vertical {
  .v-step {
    margin-bottom: 30px;
  }

  .v-step__dot {
    margin-right: 15px;
  }
}

/* Apply horizontal steps specific style. */
.v-steps:not(.v-steps--vertical) {
  .v-step {
    position: relative;
    justify-content: space-between;
    width: 100%;

    &:not(:last-of-type) {
      &.is-disabled .v-step__line,
      &:has(+ .v-step.is-disabled) .v-step__line {
        border-top: 1px dashed var(--color-grey-40);
      }

      .v-step__line {
        position: absolute;
        left: 0;
        top: 8px;
        border-top: 1px solid black;
        height: 5px;
        width: 100%;
      }
    }
  }

  .v-step__content {
    flex-direction: column;
  }

  .v-step__title {
    font-size: 14px;
    transform: translateX(calc(-50% + 8px));
    margin-top: 5px;
  }
}

.v-step__content {
  display: flex;
}

.v-step__dot {
  display: flex;
  background-color: var(--color-blue-1);
  align-items: center;
  justify-content: center;
  border-radius: 1000px;
  width: 16px;
  height: 16px;
  padding: 0 6px;
  font-size: 16px;
  color: var(--color-grey-40);
  z-index: 1;
  border: 1px solid #000;
  transition:
    background-color var(--transition),
    color var(--transition),
    border-color var(--transition);

  .v-icon * {
    stroke: var(--color-grey-40);
  }
}

.v-step__body {
  font-weight: 500;
}

.v-step__title {
  line-height: 1;
}
</style>
